import { FC, useCallback, useEffect, useRef, useState } from 'react';
import './SinglePodcastCard.scss';
import { useAppSelector } from '../../redux/app/hooks';
import images from '../../assets/images';
import { Tooltip } from '@mui/material';
import { materialsData } from '../../utils/interface';
import { SCHOOL_ADMIN, STUDENT_ROLE } from '../../utils/constants';
import ConfirmationPopup from '../ConfirmationPopup/ConfirmationPopup';
import { isPlatform } from '@ionic/react';
import { BY_ADMIN, getScreenDimensions, saveFileMetadata } from '../../utils/helper';
import { insertMaterial } from '../../database';
import { Buffer } from 'buffer';

interface SinglePodcastCardProps {
  material: materialsData,
  keyValue: string,
  handleOpenFile?: any,
  index: number;
  DescriptionRef: any
  isDescOverflow: any
  setIsDescOverflow: any
  subCategory?: string;
  subject?: string;
  checkOverflowDescription: any
  handleMaterialActions?: any
  fileExists: boolean | undefined;
  reload: ()=>void;
}

const SinglePodcastCard: FC<SinglePodcastCardProps> = ({ checkOverflowDescription, isDescOverflow, setIsDescOverflow, DescriptionRef, material, keyValue, handleOpenFile, index, handleMaterialActions, subCategory, subject, fileExists, reload}) => {
  const {PodcastPlayButton,DeleteWithBlueBg, PencilIcon, CloudDownload, ShareIcon, ShareGif, DeleteGif, ResetCode, DownloadSuccess, DownloadFailed} = images;
  const baseUrl = process.env.REACT_APP_FRONTEND_URL;
  const audioRef:any = useRef();
  const [duration,setDuration] = useState<string>('0:00 mins');
  const role = useAppSelector((state) => state.auth.role);
  const [action, setAction] = useState<string>();
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
  const [downloadComplete, setDownloadComplete] = useState<boolean>(false)
  const [downloadFailed, setDownloadFailed] = useState<boolean>(false)
  const [loading, setLoading] = useState(false); 
  const profile = useAppSelector((state) => state.auth.adminProfile);
  const confirmationMessageShare = role === SCHOOL_ADMIN ?  "Are you sure you want to Share this file with your Class & Teacher?": "Are you sure you want to Share this file with your Class?"
  const confirmationMessageDelete = role === SCHOOL_ADMIN ?  "Are you sure you want to Delete this file?": "Are you sure you want to Delete this file with your Class?"

  const onLoadedMetadata = () => {
    if (audioRef.current) {
      const totalSeconds = Math.floor(audioRef.current.duration);
      if (isNaN(totalSeconds) || totalSeconds < 0) {
        setDuration('0:00');
        return;
      }
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hours > 0) {
        setDuration(`${hours < 2 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds} ${hours < 2 ? 'hr' : 'hrs'}`);
      } else if (minutes > 0) {
        setDuration(`${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds} min`);
      } else {
        setDuration(`00:${seconds < 10 ? '0' : ''}${seconds} sec`);
      }
    }
  };
  

  const handleClick = () => {
      handleOpenFile(material);
  }

  const onYesButtonClick = () => {
    setOpenConfirmationPopup(false);
    handleMaterialActions(material, action);
  }

  const onNoButtonClick = () => {
      setOpenConfirmationPopup(false);
  }

  const handleShareDelete = (e: any, action: string) => {
    e.stopPropagation();
    setAction(action);
    if (action === "unshare" || action === "edit") {
      handleMaterialActions(material, action);
    }
    else {
      setOpenConfirmationPopup(true);
    }
  }

  useEffect(() => {
    onLoadedMetadata();
  }, [onLoadedMetadata]);

  useEffect(() => {
    checkOverflowDescription(index);
  }, []);

  const handleDownload = async (material: any) => {
    setLoading(true)
    try {
      const url = baseUrl + material?.filepath;

      const fileName = material?.filepath.split('/').pop();

      const response = await fetch(url);

      if (!response.ok) {
        console.error('Failed to fetch the file', response.statusText);
        throw new Error('File download failed');
      }

      const blob = await response.blob();

      if(window?.cordova){
        window.resolveLocalFileSystemURL(window?.cordova?.file?.dataDirectory, (dir: any) => {
          dir.getFile(fileName, { create: true }, (fileEntry: any) => {
            fileEntry.createWriter((fileWriter: any) => {
              fileWriter.onwriteend = () => {
                saveFileMetadata(fileName, { subCategory, subject });
                fileEntry.file(() => {
                  console.log('File exists and is accessible');
                  setDownloadComplete(true)
                  setTimeout(() => {
                    setDownloadComplete(false)
                  }, 5000);
                }, (error: any) => {
                  console.error('File does not exist after write', error);
                  setDownloadFailed(true);
                  setTimeout(() => {
                    setDownloadFailed(false);
                  }, 5000);
                  throw new Error('File verification failed');
                });
              };
              fileWriter.onerror = (e: any) => {
                console.error('File write failed', e);
                setDownloadFailed(true);
                setTimeout(() => {
                  setDownloadFailed(false);
                }, 5000);
              };
              fileWriter.write(blob);
            }, (error: any) => {
              console.error('Failed to create writer', error);
              setDownloadFailed(true);
              setTimeout(() => {
                setDownloadFailed(false);
              }, 5000);
              throw new Error('File creation failed');
            });
          }, (error: any) => {
            console.error('Failed to get file entry', error);
            setDownloadFailed(true);
            setTimeout(() => {
              setDownloadFailed(false);
            }, 5000);
            throw new Error('File entry access failed');
          });
        });
      }
    } catch (error) {
      console.error('Download error:', error);
      setDownloadFailed(true)
      setTimeout(() => {
        setDownloadFailed(false)
      }, 5000);
    }finally {
      setLoading(false);  // Stop loading
    }
  };
  

  const handleDownloadAudio = async (event:any) => {
    setLoading(true); 
    event.stopPropagation();
    const url = baseUrl + material?.filepath;

    // if(isPlatform('android')){
    //   try {
                
    //     const response = await fetch(url);
    //     if (!response.ok) throw new Error('File download failed');
        
    //     const blob = await response.blob();
    //     const arrayBuffer = await blob.arrayBuffer();
    //     const fileData = new Uint8Array(arrayBuffer);

    //     console.log(fileData,'=====fileData');

    //     // Insert material data into the database
    //     const materialData = {
    //         fileId: material.fileId,
    //         userId: profile?.getCurrentUser?.nodes[0]?.uId,
    //         filename: material.filename,
    //         filetype: 'audio',
    //         filepath: material.filepath,
    //         filesize: material.filesize,
    //         filedata: fileData,
    //         fileLocation: subCategory,
    //         subject: subject,
    //     };

    //     console.log(materialData,'=====materialData');
        
    //     await insertMaterial(materialData);

    //     console.log('Download and metadata storage successful');
    //     setDownloadComplete(true)
    //     reload();
    //     setTimeout(() => {
    //       setDownloadComplete(false)
    //     }, 5000);
    //   } catch (error) {
    //     console.error('Download error:', error);
    //     setTimeout(() => {
    //         setDownloadFailed(false);
    //     }, 5000);
    // } finally {
    //   setLoading(false);
    // }
    // }
      try {
        if (window?.electron && window.electron.isElectron()) {
        
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error('File download failed');
          }
    
          const blob = await response.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const fileData = Buffer.from(arrayBuffer);
    
          const saveResponse = await window.electron.saveMaterial(
            {
              fileId: material.fileId,
              userId: profile?.getCurrentUser?.nodes[0]?.uId,
              filename: material.filename,
              filetype: 'mp3',
              filepath: material.filepath,
              filesize: material.filesize,
              filedata: fileData,
              fileLocation: subCategory,
              subject: subject,
            },
          );
        if (saveResponse.success === false){
            throw new Error(saveResponse.message || 'Error saving material to SQLite');
        }
    
          console.log('File download and metadata storage successful');
    
          setDownloadComplete(true);
          reload();
          setTimeout(() => {
            setDownloadComplete(false);
          }, 5000);
    
        } else {
          // If not on Electron, fallback to normal download
          try {
            // Fetch the audio file as a blob
            const audiourl = baseUrl + material?.filepath;

            const response = await fetch(audiourl);
            // const response = await fetch(`https://invalid-url.com/nonexistentfile.mp3`);


            if (!response.ok) {
              console.error('Failed to fetch the file', response.statusText);
              setDownloadFailed(true)
              setTimeout(() => {
                setDownloadFailed(false)
              }, 5000);
              return
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            // Create a link element, simulate a click to download the file
            const a = document.createElement('a');
            a.href = url;
            a.download = material.filepath || 'audio.mp3'; // Use the filename from the material or default to 'audio.mp3'
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Release the object URL
            window.URL.revokeObjectURL(url);
            // await new Promise(resolve => setTimeout(resolve, 200));
            setDownloadComplete(true)
            setTimeout(() => {
              setDownloadComplete(false)
            }, 5000);
          } catch (error) {
            console.error('Error downloading file:', error);
            setDownloadFailed(true)
            setTimeout(() => {
              setDownloadFailed(false)
            }, 5000);
          }
        }
        
    } catch(error){
      console.error('Download error:', error);
        setTimeout(() => {
            setDownloadFailed(false);
        }, 5000);
    }
    finally {
      setLoading(false);  // Stop loading
    }
  };
  
  // Fetch the audio file as a blob
      //   const audiourl = baseUrl + material?.filepath;
  
      //   const response = await fetch(audiourl);
      //   // const response = await fetch(`https://invalid-url.com/nonexistentfile.mp3`);
  
        
      //   if (!response.ok) {
      //     console.error('Failed to fetch the file', response.statusText);
      //     setDownloadFailed(true)
      //     setTimeout(() => {
      //       setDownloadFailed(false)
      //     }, 5000);
      //     return
      //   }
  
      //   const blob = await response.blob();
      //   const url = window.URL.createObjectURL(blob);
  
      //   // Create a link element, simulate a click to download the file
      //   const a = document.createElement('a');
      //   a.href = url;
      //   a.download = material.filepath || 'audio.mp3'; // Use the filename from the material or default to 'audio.mp3'
      //   document.body.appendChild(a);
      //   a.click();
      //   document.body.removeChild(a);
  
      //   // Release the object URL
      //   window.URL.revokeObjectURL(url);
      //   // await new Promise(resolve => setTimeout(resolve, 200));
      //   setDownloadComplete(true)
      //   setTimeout(() => {
      //     setDownloadComplete(false)
      //   }, 5000);
      // } catch (error) {
      //   console.error('Error downloading file:', error);
      //   setDownloadFailed(true)
      //   setTimeout(() => {
      //     setDownloadFailed(false)
      //   }, 5000);

  // const handleDownloadAudio = async (event:any) => {
  //   setLoading(true); 
  //   event.stopPropagation()
  //   try {
  //     // Fetch the audio file as a blob
  //     const audiourl = baseUrl + material?.filepath;

  //     const response = await fetch(audiourl);
  //     // const response = await fetch(`https://invalid-url.com/nonexistentfile.mp3`);

      
  //     if (!response.ok) {
  //       console.error('Failed to fetch the file', response.statusText);
  //       setDownloadFailed(true)
  //       setTimeout(() => {
  //         setDownloadFailed(false)
  //       }, 5000);
  //       return
  //     }

  //     const blob = await response.blob();
  //     const url = window.URL.createObjectURL(blob);

  //     // Create a link element, simulate a click to download the file
  //     const a = document.createElement('a');
  //     a.href = url;
  //     a.download = material.filepath || 'audio.mp3'; // Use the filename from the material or default to 'audio.mp3'
  //     document.body.appendChild(a);
  //     a.click();
  //     document.body.removeChild(a);

  //     // Release the object URL
  //     window.URL.revokeObjectURL(url);
  //     // await new Promise(resolve => setTimeout(resolve, 200));
  //     setDownloadComplete(true)
  //     setTimeout(() => {
  //       setDownloadComplete(false)
  //     }, 5000);
  //   } catch (error) {
  //     console.error('Error downloading file:', error);
  //     setDownloadFailed(true)
  //     setTimeout(() => {
  //       setDownloadFailed(false)
  //     }, 5000);
  //   }finally {
  //     setLoading(false);  // Stop loading
  //   }
  // };

  const download = () => {
      return (
        <div className="anchorStyle" onClick={handleDownloadAudio}>
          {loading ? (
            <div className="loader"></div>
          ) : (
            <img
              src={
                (downloadComplete || fileExists)
                  ? DownloadSuccess
                  : downloadFailed
                  ? DownloadFailed
                  : CloudDownload
              }
              alt="downloadPodcast"
              className="PodcastDownload"
            />
          )}
        </div>
      );
  }


  const textRef = useRef<HTMLLabelElement>(null);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);

  const checkEllipsis = useCallback(() => {
    if (textRef.current) {
      setIsEllipsisActive(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkEllipsis();
    window.addEventListener("resize", checkEllipsis);
    return () => window.removeEventListener("resize", checkEllipsis);
  }, [checkEllipsis, material?.filename]);

  return (
    <div className='podcastCtn' key={keyValue}>
    {(index !== 0 && !getScreenDimensions()?.isMobileScreen)  && <span className='podcastTopLinebar'></span>}
    <div className='singlePodcastCardContainer' key={keyValue} onClick={handleClick}>
      <div className='podcastLeftPart'>
       <img src={PodcastPlayButton} alt='podcastbtn' className='PodcastPlayButton'/>
       <div className='podcastFilenameCtn'>
       <Tooltip title={isEllipsisActive ? material?.filename : ""} arrow>
      <label
        ref={textRef}
        className="podcastFilename"
      >
        {material?.filename}
      </label>
    </Tooltip>        { getScreenDimensions()?.isMobileScreen ?
         <div className='podcastDuration'>{duration}</div>
        :
         <Tooltip 
            title={material?.fileDescription} 
            disableHoverListener={!isDescOverflow[index]}  
        >
            <div 
              className='podcastDescription'
              ref={(el) => (index || index === 0) && (DescriptionRef.current[index] = el)}
              onMouseMove={() => {
                (index || index === 0) && checkOverflowDescription(index)
              }}
            >
              {material?.fileDescription}
            </div>
        </Tooltip>}
       </div>
      </div>
      {!getScreenDimensions()?.isMobileScreen && <div className='podcastDuration'>{duration}</div>}
      <div className='podcastActions'>
        {(role !== STUDENT_ROLE && !material.sharedBy) && 
         <img src={PencilIcon} alt='editPodcast' className='PodcastShare' onClick={(e) => handleShareDelete(e, "edit")}/>
        }
         {(role !== STUDENT_ROLE && !material.sharedBy) && 
         <img src={DeleteWithBlueBg} alt='deletePodcast' className='PodcastDelete' onClick={(e) => handleShareDelete(e, "delete")}/>
         }
         {download()}
        {(role !== STUDENT_ROLE && !material.sharedBy) ? <img src={material?.isclassroom ? ResetCode : ShareIcon} alt='sharePodcast' className='PodcastShare' onClick={(e) => handleShareDelete(e, material?.isclassroom ? "unshare" : "share")}/> :  material.sharedBy && <div className='Admin-label'>{BY_ADMIN}</div>}
      </div>
    </div>
    {getScreenDimensions()?.isMobileScreen && 
    <Tooltip 
            title={material?.fileDescription} 
            disableHoverListener={!isDescOverflow[index]}  
        >
            <div 
              className='podcastDescription'
              ref={(el) => (index || index === 0) && (DescriptionRef.current[index] = el)}
              onMouseMove={() => {
                (index || index === 0) && checkOverflowDescription(index)
              }}
            >
              {material?.fileDescription}
            </div>
      </Tooltip>}
    <audio
      ref={audioRef}
      onLoadedMetadata={onLoadedMetadata}
      className='audioStyle'
    >
       <source src={baseUrl + material?.filepath} type="audio/mpeg" />
        <source src={baseUrl + material?.filepath} type="audio/wav" />
        <source src={baseUrl + material?.filepath} type="audio/ogg" />
    </audio>
    <ConfirmationPopup
        open={openConfirmationPopup}
        message={action==="share" ?  confirmationMessageShare: confirmationMessageDelete}
        gifData={action === "share" ? ShareGif : DeleteGif}
        yesButtonFunctionality={onYesButtonClick}
        noButtonFunctionality={onNoButtonClick}
        messageWidth={'210px'}
    />
    </div>
  )
}

export default SinglePodcastCard;
